import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Input,
  Select,
  Tooltip,
  Alert,
  Typography,
  Switch,
  Modal,
} from 'antd';
import Icon, {
  CopyOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  GlobalOutlined,
  CheckOutlined,
  TeamOutlined,
} from '@ant-design/icons';

import FilterField from './FilterField/';
import { STATUS } from '../../redux/constants';
import styles from './filter-profile-details.module.sass';
import cx from 'classnames';
import { showNotification, successNotification } from '../Notifications';
import mixpanel from 'mixpanel-browser';
import { authClient } from '../../redux/api/Client';
import CopyEndpoint from '../CopyEndpoint';
import { Link, useLocation } from 'react-router-dom';
import history from '../../redux/history';
import LoadingSkeleton from '../LoadingSkeleton';
import LoadingSilentPush from '../LoadingSilentPush';
import { incrementUIRequestsCounter } from '../../redux/utils/websocket/incrementUIRequestsCounter';
import { useDispatch } from 'react-redux';

const FilterProfileDetails = (props) => {
  const scopeIconMapping = {
    USER: <UserOutlined />,
    ORGANISATION: <TeamOutlined />,
    GLOBAL: <GlobalOutlined />,
  };

  const {
    loadMySubscriptionPlan,
    availableFilterProfiles,
    originalFilterProfiles,
    appliedFilterProfile,
    originalAppliedFilterProfile,
    dropdownSelectedProfile,
    filterIndicators,
    mySubscriptionPlan,
    profilesLoadStatus,
    profilesUpdateStatus,
    deleteProfileStatus,
    paginationStatus,
    loadSpecificFilter,
    specificFilterIndicator,
    specificFilterSettingsStatus,
    resetSpecificFilter,
    loadTagOptions,
    autoCompleteTagOptions,
    resetTagOptions,
    tagOptions,
    tagOptionsStatus,
    indicatorsMap,
    filterSidebarOpen,
    filterSidebarStatus,
    profileChangedNameStatus,
    optionsByIndicator,
    user,
  } = props;

  const { Text, Title } = Typography;
  const MAX_BLOCKS_LENGTH = 5;

  const q = useQuery();
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [canCreateFilterProfile, setCanCreateFilterProfile] = useState(false);
  const [nameInputValue, setNameInputValue] = useState('');
  const [nameIsValid, setNameIsValid] = useState(true);
  const [createNewState, setCreateNewState] = useState(false);
  const [editingState, setEditingState] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [alerts, setAlerts] = useState(true);
  const dispatch = useDispatch();
  const [fpReseted, setFpReseted] = useState(false);
  const [
    showDeleteFilterProfileModal,
    setShowDeleteFilterProfileModal,
  ] = useState(false);
  const [numberInputError, setNumberInputError] = useState(false);
  let appliedFilterUUID = q?.get('filterProfile')?.split('?')[0];

  useEffect(() => {
    loadMySubscriptionPlan();
    setCanCreateFilterProfile(
      user?.subscription_info?.can_create_filter_profile,
    );

    //open advanced filter left menu and set redux state to create
    if (window.location.search.includes('?openCreateNewAdvancedFilter=true')) {
      props.openCreateNewAdvancedFilter();
      fetchData(null);
    }

    return () => {
      //close advanced filter menu on navigating away
      props.closeFilterSidebar();
    };
  }, []);

  useEffect(() => {
    if (
      dropdownSelectedProfile?.name &&
      profileChangedNameStatus === STATUS.SUCCESS
    ) {
      setNameInputValue(dropdownSelectedProfile?.name);
      props.loadFilterProfiles();
    }
  }, [profileChangedNameStatus]);

  useEffect(() => {
    if (availableFilterProfiles && deleteProfileStatus === STATUS.SUCCESS) {
      props.changeSelectedFilterProfile(availableFilterProfiles?.[0]);
    }
  }, [deleteProfileStatus]);

  useEffect(() => {
    setCanCreateFilterProfile(
      user?.subscription_info?.can_create_filter_profile,
    );

    // available filter profiles change
    if (
      availableFilterProfiles?.length > 0 &&
      dropdownSelectedProfile === null &&
      !window.location.search.includes('?openCreateNewAdvancedFilter=true')
    ) {
      // on first load if nothing is selected, select the first available fp
      props.changeSelectedFilterProfile(availableFilterProfiles?.[0]);
    } else if (
      window.location.search.includes('?openCreateNewAdvancedFilter=true')
    ) {
      // when it is loaded in create new state
      setCreateNewState(true);
      setNameIsValid(true);
      document.title = `Threat Ranking - Silent Push`;
      props.changeSelectedFilterProfile(returnBlankFilterProfile());
    }

    if (dropdownSelectedProfile && !createNewState && !appliedFilterUUID) {
      const matchingFp = availableFilterProfiles.filter(
        (fp) => fp?.uuid === dropdownSelectedProfile?.uuid,
      )[0];

      if (window.location.search.includes('?newFp=true')) {
        props.changeSelectedFilterProfile(
          availableFilterProfiles[availableFilterProfiles?.length - 1],
        );

        window.history.replaceState(
          null,
          '',
          `${window.location.search.replace('?newFp=true', '')}`,
        );
      } else if (
        matchingFp?.scope !== dropdownSelectedProfile?.scope &&
        matchingFp
      ) {
        props.changeSelectedFilterProfile(matchingFp);
      }
    }
  }, [availableFilterProfiles]);

  useEffect(() => {
    if (filterSidebarOpen) {
      //fallback if user is in edit mode and there is no fp selected
      if (
        dropdownSelectedProfile?.uuid === undefined &&
        filterSidebarStatus === 'edit' &&
        profilesLoadStatus === STATUS.LOADED
      ) {
        props.changeSelectedFilterProfile(availableFilterProfiles?.[0]);
      }

      if (
        availableFilterProfiles?.length > 0 &&
        !window.location.search.includes('?openCreateNewAdvancedFilter=true')
      ) {
        setCreateNewState(false);
      }
    }
  }, [filterSidebarOpen]);

  useEffect(() => {
    setAlerts(
      dropdownSelectedProfile?.send_alert
        ? dropdownSelectedProfile?.send_alert
        : false,
    );
    if (!createNewState) {
      setNameInputValue(dropdownSelectedProfile?.name);
    }
  }, [dropdownSelectedProfile?.send_alert, dropdownSelectedProfile?.name]);

  useEffect(() => {
    //switching between create new and an existing filter profile
    if (
      appliedFilterUUID &&
      dropdownSelectedProfile?.uuid === undefined &&
      !window.location.search.includes('?openCreateNewAdvancedFilter=true')
    ) {
      setCreateNewState(false);
      setHasPendingChanges(false);
      setAlerts(
        appliedFilterProfile?.send_alert
          ? appliedFilterProfile?.send_alert
          : false,
      );
      props.changeSelectedFilterProfile(appliedFilterProfile);
    } else if (
      window.location.search.includes('?openCreateNewAdvancedFilter=true') &&
      !createNewState
    ) {
      // when it is loaded in create new state
      setCreateNewState(true);
      setHasPendingChanges(false);
      setNameInputValue('');
      setNameIsValid(true);
      setAlerts(true);
      document.title = `Threat Ranking - Silent Push`;
      props.changeSelectedFilterProfile(returnBlankFilterProfile());
    }

    //re-loading create new state from the left menu
    if (
      window.location.search.includes('?openCreateNewAdvancedFilter=true') &&
      window.location.search.includes('?fromMenu=true')
    ) {
      window.history.replaceState(
        null,
        '',
        '/?openCreateNewAdvancedFilter=true?openAdvancedFilter=true',
      );
      setNameIsValid(true);
      setNameInputValue('');
    }

    //apply filter profile if it was applied before or coming from an external link
    if (
      appliedFilterUUID &&
      originalFilterProfiles?.length > 0 &&
      appliedFilterProfile?.uuid !== appliedFilterUUID
    ) {
      const origin = JSON.parse(JSON.stringify(originalFilterProfiles));
      const matchingFp = origin?.filter(
        (fp) => fp?.uuid === appliedFilterUUID,
      )[0];
      setCreateNewState(false);
      setAlerts(matchingFp?.send_alert ? matchingFp?.send_alert : false);

      const conditionsEqual =
        JSON.stringify(dropdownSelectedProfile?.condition_blocks) ===
        JSON.stringify(matchingFp?.condition_blocks);

      if (
        !conditionsEqual &&
        appliedFilterUUID === dropdownSelectedProfile?.uuid
      ) {
        fetchData(dropdownSelectedProfile);
      } else {
        if (matchingFp) {
          setHasPendingChanges(false);
          fetchData(matchingFp);
          document.title = `${
            matchingFp.name ? matchingFp.name : 'Threat Ranking'
          } - Silent Push`;
          props.changeSelectedFilterProfile(matchingFp);
        }
      }
    }
  }, [appliedFilterProfile, originalFilterProfiles, window.location.search]);

  const fetchData = (fp) => {
    props.applyFilters(fp);
    incrementUIRequestsCounter();
  };

  const validate = async (exclude) => {
    if (nameInputValue?.length > 0 || exclude) {
      setNameIsValid(true);
    } else {
      setNameIsValid(false);
    }

    await Promise.all(
      dropdownSelectedProfile?.condition_blocks?.map(async (block) => {
        return Promise.all(
          block.conditions.map(async (field) => {
            if (field.ref?.current) {
              await field.ref.current.validateFields();
            }
          }),
        );
      }),
    );
  };

  function useQuery() {
    return new URLSearchParams(useLocation().search);
  }

  function returnBlankFilterProfile() {
    const blankExpression = [
      {
        id: Math.random(),
        conditions: [
          { ref: React.createRef(), id: Math.random(), position: 1 },
        ],
        position: 1,
      },
    ];
    const newProfile = {
      condition_blocks: blankExpression,
      is_mine: true,
      name: '',
      scope: 'USER',
      send_alert: true,
      send_notification: false,
      tags: [],
    };

    return newProfile;
  }

  function handleReset() {
    const origin = JSON.parse(JSON.stringify(originalFilterProfiles));
    const matchingFp = origin.filter(
      (fp) => fp?.uuid === dropdownSelectedProfile?.uuid,
    )[0];

    setHasPendingChanges(false);
    setNameIsValid(true);
    setFpReseted(true);
    setNumberInputError(false);

    if (createNewState) {
      setNameInputValue('');
      props.resetSelectedFilterProfile(returnBlankFilterProfile());
    } else {
      props.resetSelectedFilterProfile(matchingFp);
    }

    props.loadFilterProfiles();
  }

  const handleApplyClick = async () => {
    const origin = JSON?.parse(JSON?.stringify(originalFilterProfiles));
    const matchingFp = origin?.filter(
      (fp) => fp?.uuid === appliedFilterUUID,
    )[0];
    let conditionsEqual =
      JSON.stringify(dropdownSelectedProfile?.condition_blocks) ===
      JSON.stringify(matchingFp?.condition_blocks);

    //if the applied filter profile no longer matches the selected one
    if (
      JSON.stringify(originalAppliedFilterProfile?.condition_blocks) !==
      JSON.stringify(dropdownSelectedProfile?.condition_blocks)
    ) {
      conditionsEqual = false;
    }

    try {
      //the boolean is for exclude FP name validation
      await validate(true);

      //apply only if the user stays on the same filter profile but the conditions change
      if (
        (!conditionsEqual &&
          appliedFilterUUID === dropdownSelectedProfile?.uuid) ||
        fpReseted
      ) {
        setFpReseted(false);
        fetchData(dropdownSelectedProfile);
        return;
      }

      history.push(
        '/?filterProfile=' +
          dropdownSelectedProfile?.uuid +
          '?openAdvancedFilter=true',
      );

      document.title = `${
        dropdownSelectedProfile.name
          ? dropdownSelectedProfile.name
          : 'Threat Ranking'
      } - Silent Push`;
    } catch (err) {}
  };

  const handleSaveClick = async (saveTag = false) => {
    const updatedFilterProfile = availableFilterProfiles.filter(
      (fp) => fp?.uuid === dropdownSelectedProfile?.uuid,
    )[0];

    /* if (saveTag) {
      updatedFilterProfile.tag_name = dropdownSelectedProfile?.tag;
      updatedFilterProfile.tag_save = true;
      updatedFilterProfile.expiration = dropdownSelectedProfile?.tagDuration
        ? dropdownSelectedProfile?.tagDuration.toString()
        : null;
    } else if (dropdownSelectedProfile?.tags?.length > 0) {
      updatedFilterProfile.tag_name = dropdownSelectedProfile?.tags[0]?.name;
      updatedFilterProfile.tag_save = true;
      updatedFilterProfile.expiration =
        dropdownSelectedProfile?.tags[0]?.expiration;
    } else if (updatedFilterProfile) {
      updatedFilterProfile.tag_name = null;
      updatedFilterProfile.tag_save = false;
      updatedFilterProfile.expiration = null;
    } */

    try {
      await validate();
      if (dropdownSelectedProfile?.uuid) {
        props.updateFilterProfile(
          {
            ...dropdownSelectedProfile,
            uuid: dropdownSelectedProfile.uuid,
            send_alert: dropdownSelectedProfile.send_alert,
          },
          false,
        );
      } else {
        props.saveFilterProfile(
          {
            ...dropdownSelectedProfile,
            send_alert: true,
            name: nameInputValue,
          },
          false,
        );
      }

      let filterParameters = {
        Name: nameInputValue,
        'Condition Blocks': dropdownSelectedProfile?.condition_blocks,
        'Selected Profile: ': dropdownSelectedProfile,
        Tag: dropdownSelectedProfile?.tag,
        'Tag Duration': dropdownSelectedProfile?.tagDuration,
      };

      let allfilterParameters = JSON.stringify(filterParameters);
      mixpanel.track('Advance Filter Apply', {
        filters: [allfilterParameters],
      });

      setHasPendingChanges(false);

      if (!updatedFilterProfile) {
        setCreateNewState(false);
        history.push('/?openAdvancedFilter=true?newFp=true');
      }
    } catch (err) {}
  };

  const handleAddFieldClick = async (blockIndex) => {
    try {
      await this.validate();
    } catch (err) {}

    let editedFp = JSON.parse(JSON.stringify(dropdownSelectedProfile));
    const newCondition = {
      ref: React.createRef(),
      id: Math.random(),
      binary_operator: 'AND',
      position: editedFp.condition_blocks[blockIndex].conditions?.length + 1,
    };

    editedFp.condition_blocks[blockIndex].conditions.push(newCondition);
    props.changeSelectedFilterProfile(editedFp);
    setHasPendingChanges(true);
  };

  const handleAddBlockClick = async () => {
    try {
      await this.validate();
    } catch (err) {}
    let editedFp = JSON.parse(JSON.stringify(dropdownSelectedProfile));

    const newExpression = {
      id: Math.random(),
      binary_operator: 'AND',
      conditions: [{ ref: React.createRef(), id: Math.random(), position: 1 }],
      position: editedFp?.condition_blocks?.length + 1,
    };

    editedFp.condition_blocks.push(newExpression);
    props.changeSelectedFilterProfile(editedFp);
    setHasPendingChanges(true);
  };

  function handleFieldChange(blockIndex, field, index) {
    let editedFp = { ...dropdownSelectedProfile };

    editedFp.condition_blocks[blockIndex].conditions[index] = field;
    props.changeSelectedFilterProfile(editedFp);
    setHasPendingChanges(true);
  }

  function handleFieldDelete(blockIndex, index) {
    let editedFp = JSON.parse(JSON.stringify(dropdownSelectedProfile));
    let newFields = JSON.parse(
      JSON.stringify(
        dropdownSelectedProfile?.condition_blocks[blockIndex].conditions,
      ),
    );

    newFields.splice(index, 1);
    newFields = newFields.map((field, index) => ({
      ...field,
      position: index + 1,
    }));

    editedFp.condition_blocks[blockIndex].conditions = newFields;
    props.changeSelectedFilterProfile(editedFp);
    setHasPendingChanges(true);
  }

  function handleBlockDelete(blockIndex) {
    let editedFp = { ...dropdownSelectedProfile };
    let newBlocks = [...dropdownSelectedProfile?.condition_blocks];

    newBlocks.splice(blockIndex, 1);
    newBlocks = newBlocks.map((block, index) => ({
      ...block,
      position: index + 1,
    }));

    editedFp.condition_blocks = newBlocks;
    props.changeSelectedFilterProfile(editedFp);
    setHasPendingChanges(true);
  }

  function handleBlockChange(blockIndex, block) {
    let editedFp = { ...dropdownSelectedProfile };
    const newBlocks = [...dropdownSelectedProfile?.condition_blocks];
    newBlocks.splice(blockIndex, 1, block);

    editedFp.condition_blocks = newBlocks;
    props.changeSelectedFilterProfile(editedFp);
    setHasPendingChanges(true);
  }

  function handleNameChange(e) {
    setNameInputValue(e.target.value);
    setNameIsValid(e.target.value.length > 0);
  }

  function handleEditClick() {
    setEditingState(true);
  }

  function handleFinishEditClick(selectedProfile) {
    props.changeFilterProfileName(selectedProfile?.uuid, nameInputValue);
    setEditingState(false);
  }

  function handleCreateNewClick() {
    setHasPendingChanges(false);
    setCreateNewState(true);
    setNameIsValid(true);
    setNameInputValue('');
    document.title = `Threat Ranking - Silent Push`;
    props.openCreateNewAdvancedFilter();
    props.changeSelectedFilterProfile(returnBlankFilterProfile());
  }

  function handleEditStateClick() {
    setCreateNewState(false);
    props.changeSelectedFilterProfile(availableFilterProfiles?.[0]);
    props.openEditAdvancedFilter();
  }

  const handleProfileSelect = async (uuid) => {
    if (uuid) {
      const origin = JSON?.parse(JSON?.stringify(originalFilterProfiles));
      const matchingFp = origin?.filter((fp) => fp?.uuid === uuid)?.[0];

      setHasPendingChanges(false);
      props.changeSelectedFilterProfile(matchingFp);

      try {
        await validate();
        document.title = `${
          matchingFp?.name ? matchingFp?.name : 'Threat Ranking'
        } - Silent Push`;
      } catch (err) {}
    }
  };

  function getUrl() {
    let hostName = window.location.protocol + '//' + window.location.host;
    let newActiveThreatRankingEndpoint =
      props.activeThreatRankingEndpoint?.replace(
        '&limit=100&',
        '&limit=1000&',
      ) + '&advanced=simple';
    return hostName + newActiveThreatRankingEndpoint;
  }

  function assembleUrl() {
    return getUrl()
      ?.split('&')
      ?.filter((u) => u?.indexOf('filter_profile=') == -1)
      ?.join('&')
      .toString();
  }

  function handleAlertsToggle(profile) {
    setAlerts(!alerts);
    props.updateFilterProfile(
      {
        ...profile,
        send_alert: !profile.send_alert,
      },
      false,
    );
  }

  function handleShowCopyModal(visible) {
    setShowCopyModal(visible);
  }

  function returnDeleteTooltip(scope) {
    if (scope === 'GLOBAL') {
      return 'This filter profile cannot be deleted, as it is global';
    } else {
      return 'This filter profile cannot be deleted, as it is shared with your organization';
    }
  }

  function changeScopeOfFilterProfile(filterProfile) {
    if (!filterProfile.is_mine) {
      return;
    }
    const changeScopeTo =
      filterProfile.scope === 'USER' ? 'ORGANISATION' : 'USER';
    const messageMapping = {
      USER: 'Filter profile made private',
      ORGANISATION: 'Filter profile shared with organization',
    };
    authClient.User.changeScopeOfFilterProfile({
      uuid: filterProfile.uuid,
      body: {
        scope: changeScopeTo,
      },
    }).then((res) => {
      if (res.responseStatus === 200) {
        let updatedSelectedFP = dropdownSelectedProfile;
        updatedSelectedFP.scope = changeScopeTo;

        props.changeFilterProfileScope(filterProfile, changeScopeTo);
        props.changeSelectedFilterProfile(updatedSelectedFP);
        showNotification(
          successNotification({
            statusCode: 200,
            message: messageMapping[changeScopeTo],
          }),
        );
        return;
      }
    });
  }

  function getTooltipTextForSharing(filterProfile) {
    if (filterProfile.scope === 'ORGANISATION' && !filterProfile.is_mine) {
      return 'Only the creator can remove access';
    }
    return null;
  }

  function returnSaveTooltip(canCreate, fpName, pendingChanges) {
    if (!canCreate) {
      return 'You exceeded your filter profile quota';
    } else if (!pendingChanges) {
      return 'To save filter profile all required fields needs to be filled';
    } else if (!fpName) {
      return 'Please add a filter profile name';
    }
  }

  function returnApplyTooltip(fpName, pendingChanges) {
    if (pendingChanges) {
      return 'To apply filter profile all required fields needs to be filled';
    } else if (!fpName) {
      return 'Please add a filter profile name';
    }
  }

  return (
    <>
      <div className={styles.filterProfileDetails}>
        <div className={styles.changesAlert}>
          {hasPendingChanges && (
            <Alert message="There are unsaved changes." type="warning" />
          )}
        </div>

        <Row className={styles.nameInputRow} align="bottom">
          {profilesLoadStatus === STATUS.LOADING ? (
            <div className={styles.loadingConainer}>
              <LoadingSkeleton />
            </div>
          ) : (
            <Col xs={24}>
              <div className={styles.nameInputContainer}>
                <div className={styles.alertSwitch}>
                  <Text>Alerts</Text>
                  <Switch
                    disabled={createNewState}
                    defaultChecked
                    checked={alerts}
                    loading={profilesUpdateStatus === 'running'}
                    onChange={() => handleAlertsToggle(dropdownSelectedProfile)}
                  />
                </div>

                <div className={styles.nameInput}>
                  <Text>Filter Profile Name</Text>

                  <div
                    className={cx(styles.input, {
                      [styles.createState]: createNewState,
                    })}
                  >
                    {createNewState || editingState ? (
                      <div style={{ width: '100%' }}>
                        <Input
                          value={nameInputValue}
                          status={nameIsValid ? null : 'error'}
                          maxLength={40}
                          placeholder={'Add a filter profile name'}
                          onChange={(e) => handleNameChange(e)}
                          onPressEnter={() =>
                            handleFinishEditClick(dropdownSelectedProfile)
                          }
                        />
                        {!nameIsValid && (
                          <Text className={styles.inputError}>
                            Missing name
                          </Text>
                        )}
                      </div>
                    ) : (
                      <>
                        <Tooltip
                          placement="right"
                          title="Select filter profile name from dropdown list"
                        >
                          <Select
                            className={styles.nameSelect}
                            loading={profilesUpdateStatus === STATUS.RUNNING}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            placeholder={nameInputValue}
                            onSelect={(value) => {
                              if (
                                window.location.search.includes('?newFp=true')
                              ) {
                                window.history.replaceState(
                                  null,
                                  '',
                                  window.location.search.replace(
                                    '?newFp=true',
                                    '',
                                  ),
                                );
                                props.loadFilterProfiles();
                              }
                              handleProfileSelect(value);
                            }}
                            value={dropdownSelectedProfile?.uuid}
                          >
                            {availableFilterProfiles.map((profile) => (
                              <Select.Option key={profile.uuid}>
                                {profile.scope in scopeIconMapping
                                  ? scopeIconMapping[profile.scope]
                                  : ''}{' '}
                                {profile.name}
                              </Select.Option>
                            ))}
                          </Select>
                        </Tooltip>
                      </>
                    )}

                    {!createNewState && (
                      <>
                        {!editingState && (
                          <>
                            {availableFilterProfiles &&
                              dropdownSelectedProfile &&
                              dropdownSelectedProfile?.is_mine && (
                                <Tooltip title="Edit filter profile name">
                                  <EditOutlined
                                    className={styles.editNameButton}
                                    role={'button'}
                                    onClick={() => handleEditClick()}
                                  />
                                </Tooltip>
                              )}
                          </>
                        )}

                        {editingState && (
                          <Tooltip title="Save filter profile name">
                            <CheckOutlined
                              className={styles.editNameButton}
                              role={'button'}
                              onClick={() =>
                                handleFinishEditClick(dropdownSelectedProfile)
                              }
                            />
                          </Tooltip>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          )}
        </Row>

        {profilesLoadStatus === STATUS.LOADING ? (
          <Row className={styles.copyEndpointRow}>
            <Col xs={24}>
              <Button disabled={true}>Share</Button>
              <Button disabled={true} icon={<CopyOutlined />}>
                Copy API Endpoint
              </Button>
            </Col>
          </Row>
        ) : (
          availableFilterProfiles &&
          availableFilterProfiles?.length > 0 && (
            <Row className={styles.copyEndpointRow}>
              <Col xs={24}>
                {dropdownSelectedProfile &&
                  dropdownSelectedProfile?.scope !== 'GLOBAL' && (
                    <Tooltip
                      title={
                        hasPendingChanges
                          ? 'Filter profile needs to be saved to be able to share it with your organisation'
                          : getTooltipTextForSharing(dropdownSelectedProfile)
                      }
                    >
                      {dropdownSelectedProfile?.is_mine && (
                        <Button
                          disabled={
                            createNewState ||
                            !mySubscriptionPlan?.has_access_to_share_unshare ||
                            hasPendingChanges
                          }
                          onClick={() =>
                            changeScopeOfFilterProfile(dropdownSelectedProfile)
                          }
                        >
                          {dropdownSelectedProfile?.scope === 'USER'
                            ? 'Share'
                            : 'Unshare'}
                        </Button>
                      )}
                    </Tooltip>
                  )}

                <Button
                  disabled={
                    createNewState ||
                    !mySubscriptionPlan?.has_access_to_threat_ranking
                  }
                  onClick={() => setShowCopyModal(true)}
                  icon={<CopyOutlined />}
                >
                  Copy API Endpoint
                </Button>

                <CopyEndpoint
                  fpuuid={dropdownSelectedProfile?.uuid}
                  endpoint={assembleUrl()}
                  body={props.endpointParams?.body}
                  title="Copy filter profile's API Endpoint"
                  description={
                    <>
                      <Text className="mb-1">
                        To automate the filter profile to the security stack,
                        copy its API endpoint by clicking the button below. To
                        modify the endpoint use instructions from our&nbsp;
                        <a href="http://docs.silentpush.com">
                          API documentation
                        </a>
                      </Text>

                      {hasPendingChanges && (
                        <Alert
                          className="mt-1"
                          message="Unsaved changes in the filter profile won't be included"
                          type="warning"
                        />
                      )}
                    </>
                  }
                  visible={showCopyModal}
                  handleShowAutomateExportModal={handleShowCopyModal}
                />
              </Col>
            </Row>
          )
        )}

        {profilesLoadStatus === STATUS.LOADING ? (
          <div className={styles.loadingContainer}>
            <Title level={5}>Logical Expressions</Title>

            <div className={styles.loadingExpressions}>
              <LoadingSilentPush size={50} background={'dark'} />
            </div>
          </div>
        ) : (
          availableFilterProfiles?.length > 0 && (
            <Row className="mt-1">
              <Title level={5}>Logical Expressions</Title>

              {dropdownSelectedProfile?.condition_blocks &&
                dropdownSelectedProfile?.condition_blocks?.map(
                  (block, blockIndex) => (
                    <Col
                      xs={24}
                      className={styles.logicalExpression}
                      key={block.uuid || block.id}
                    >
                      {dropdownSelectedProfile?.condition_blocks?.length >
                        1 && (
                        <Tooltip title={'Remove logical expression'}>
                          <DeleteOutlined
                            className={styles.deleteLogicalExpression}
                            role={'button'}
                            onClick={() => handleBlockDelete(blockIndex)}
                          />
                        </Tooltip>
                      )}

                      <Row className={styles.container} gutter={12}>
                        {block &&
                          block?.conditions?.map((field, index) => (
                            <FilterField
                              indicatorsMap={indicatorsMap}
                              ref={field.ref}
                              key={field.id}
                              hideBinaryOperator={index === 0}
                              field={field}
                              options={filterIndicators}
                              loadSpecificFilter={loadSpecificFilter}
                              resetSpecificFilter={resetSpecificFilter}
                              specificFilterIndicator={specificFilterIndicator}
                              specificFilterSettingsStatus={
                                specificFilterSettingsStatus
                              }
                              loadTagOptions={loadTagOptions}
                              autoCompleteTagOptions={autoCompleteTagOptions}
                              resetTagOptions={resetTagOptions}
                              tagOptions={tagOptions}
                              tagOptionsStatus={tagOptionsStatus}
                              optionsByIndicator={optionsByIndicator}
                              binary_operator={field.binary_operator}
                              canDelete={block.conditions?.length > 1}
                              onDelete={() =>
                                handleFieldDelete(blockIndex, index)
                              }
                              onChange={(field) =>
                                handleFieldChange(blockIndex, field, index)
                              }
                              indicator={field.indicator}
                              operator={field.operator}
                              value={field.value}
                              expressionIndex={blockIndex}
                              conditionIndex={index}
                              setNumberInputError={setNumberInputError}
                              numberInputError={numberInputError}
                            />
                          ))}

                        <Col xs={24} className={styles.conditionActions}>
                          {block.conditions?.length < MAX_BLOCKS_LENGTH && (
                            <Tooltip title="Add new condition">
                              <Button
                                role={'button6'}
                                type="link"
                                onClick={() => handleAddFieldClick(blockIndex)}
                              >
                                Add new
                              </Button>
                            </Tooltip>
                          )}
                        </Col>

                        {dropdownSelectedProfile?.condition_blocks?.length >
                          1 && (
                          <Select
                            className={styles.expressionOperator}
                            getPopupContainer={(trigger) => trigger.parentNode}
                            defaultActiveFirstOption
                            value={block.binary_operator}
                            onChange={(value) => {
                              handleBlockChange(blockIndex, {
                                ...block,
                                binary_operator: value,
                              });
                            }}
                          >
                            <Select.Option
                              role={'button'}
                              aria-label={'andToggle'}
                              value="AND"
                            >
                              AND
                            </Select.Option>
                            <Select.Option
                              role={'button'}
                              aria-label={'orToggle'}
                              value="OR"
                            >
                              OR
                            </Select.Option>
                          </Select>
                        )}
                      </Row>
                    </Col>
                  ),
                )}
            </Row>
          )
        )}

        <Row
          type="flex"
          align="center"
          justify="space-between"
          className={styles.logicalExpressionActions}
        >
          {dropdownSelectedProfile?.condition_blocks?.length <
            MAX_BLOCKS_LENGTH && (
            <Form.Item>
              <Tooltip title="Add new logical expression">
                <Button
                  className={styles.newLogicalExression}
                  role={'addNewLogicalExpressionButton'}
                  type="link"
                  onClick={() => handleAddBlockClick()}
                >
                  New Logical Expression
                </Button>
              </Tooltip>
            </Form.Item>
          )}

          <div>
            <Tooltip
              title={
                hasPendingChanges
                  ? 'Reset the filter profile'
                  : 'Filter profile has no changes'
              }
            >
              <Button
                role={'resetButton'}
                disabled={!hasPendingChanges}
                onClick={() => handleReset()}
              >
                Reset All
              </Button>
            </Tooltip>

            <Tooltip
              title={returnApplyTooltip(nameInputValue, hasPendingChanges)}
              getPopupContainer={(triggerNode) => triggerNode.parentNode}
            >
              <Button
                className="ms-05"
                type="primary"
                loading={
                  props.status === STATUS.LOADING ||
                  paginationStatus === STATUS.LOADING
                }
                role={'button2'}
                onClick={() => handleApplyClick()}
                disabled={numberInputError}
              >
                Apply
              </Button>
            </Tooltip>
          </div>
        </Row>
      </div>

      <Row className={cx(styles.filterProfileFooter)} type="flex">
        {!createNewState && (
          <div className={styles.createNewButton}>
            <Link
              to={`/?openAdvancedFilter=true?openCreateNewAdvancedFilter=true`}
            >
              <Tooltip title="Create a new filter profile">
                <Button
                  role={'createNewButton'}
                  onClick={() => handleCreateNewClick()}
                >
                  Create New
                </Button>
              </Tooltip>
            </Link>
          </div>
        )}

        {createNewState && (
          <div className={styles.createNewButton}>
            <Link to={`/?openAdvancedFilter=true?`}>
              <Tooltip title="Select filter profiles">
                <Button
                  role={'selectFilterProfilesButton'}
                  onClick={() => handleEditStateClick()}
                >
                  Select Filter Profiles
                </Button>
              </Tooltip>
            </Link>
          </div>
        )}

        {!createNewState && dropdownSelectedProfile?.is_mine && (
          <Tooltip
            title={
              dropdownSelectedProfile?.scope === 'ORGANISATION' ||
              dropdownSelectedProfile?.scope === 'GLOBAL'
                ? returnDeleteTooltip(dropdownSelectedProfile?.scope)
                : 'Delete filter profile'
            }
          >
            <Button
              role={'button5'}
              danger
              type="default"
              disabled={
                dropdownSelectedProfile?.scope === 'ORGANISATION' ||
                dropdownSelectedProfile?.scope === 'GLOBAL' ||
                profilesLoadStatus === STATUS.LOADING ||
                paginationStatus === STATUS.LOADING
              }
              onClick={() => setShowDeleteFilterProfileModal(true)}
            >
              Delete
            </Button>
          </Tooltip>
        )}

        {createNewState && (
          <Tooltip
            title={returnSaveTooltip(
              canCreateFilterProfile,
              nameInputValue,
              hasPendingChanges,
            )}
          >
            <Button
              className="ms-05"
              role={'button4'}
              type="primary"
              disabled={
                !canCreateFilterProfile || !nameInputValue || !hasPendingChanges
              }
              onClick={() => handleSaveClick()}
            >
              Save
            </Button>
          </Tooltip>
        )}

        {!createNewState && (
          <Tooltip
            title={
              !hasPendingChanges
                ? 'There are no new changes on filter profile to be updated'
                : dropdownSelectedProfile?.is_mine
                ? 'Update filter profile'
                : "This filter profile wasn't authored by you. You have the ability to modify only those filter profiles that you've created"
            }
          >
            <Button
              className="ms-05"
              role={'button5'}
              type="primary"
              disabled={!dropdownSelectedProfile?.is_mine || !hasPendingChanges}
              onClick={() => handleSaveClick()}
            >
              Update
            </Button>
          </Tooltip>
        )}
      </Row>

      <Modal
        bodyStyle={{ padding: 24 }}
        title={'Delete Filter Profile'}
        open={showDeleteFilterProfileModal}
        onOk={() => {
          props.deleteFilterProfile(dropdownSelectedProfile?.uuid);
          setShowDeleteFilterProfileModal(false);

          window.history.replaceState(null, '', '/?openAdvancedFilter=true');
          setHasPendingChanges(false);
          setNumberInputError(false);

          //props.changeSelectedFilterProfile(availableFilterProfiles?.[0]);
        }}
        onCancel={() => setShowDeleteFilterProfileModal(false)}
        okText="Delete"
        okType="danger"
        className={styles.deleteFPModal}
      >
        <Title level={5}>
          Are you sure you want to delete {dropdownSelectedProfile?.name} filter
          profile?
        </Title>

        <Text>You are about to permanently delete your filter profile.</Text>
        <br />
        <Text>You can't undo this action!</Text>
      </Modal>
    </>
  );
};

export default FilterProfileDetails;
